import { LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { finalize } from 'rxjs';
import { timeAgo } from '../../core/functions/relative-time';
import { AuthService } from '../../core/services/auth/auth.service';
import { User, UserRole } from '../../core/models/user';
import { AdminUsersService } from '../../core/services/user/admin-users.service';
import { DialogService, ToastService } from '../../ui';
import { InviteUserDialogComponent } from './invite-user-dialog.component';

type RoleFilter = 'All' | UserRole | 'Invited';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [LowerCasePipe, RouterLink, CdkMenuTrigger, CdkMenu, CdkMenuItem],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminComponent implements OnInit {
  private adminUsers = inject(AdminUsersService);
  private toast = inject(ToastService);
  private dialog = inject(DialogService);
  private auth = inject(AuthService);

  readonly roles: UserRole[] = ['Admin', 'Lead', 'Tester', 'Viewer'];
  readonly filters: { key: RoleFilter; label: string }[] = [
    { key: 'All', label: 'All' },
    { key: 'Admin', label: 'Admins' },
    { key: 'Lead', label: 'Leads' },
    { key: 'Tester', label: 'Testers' },
    { key: 'Viewer', label: 'Viewers' },
    { key: 'Invited', label: 'Invited' },
  ];

  readonly users = signal<User[]>([]);
  readonly isLoading = signal(false);
  /** Placeholder rows rendered while users load. */
  readonly skeletonRows = [0, 1, 2, 3, 4];
  readonly search = signal('');
  readonly roleFilter = signal<RoleFilter>('All');

  private readonly currentUserId = computed(() => this.auth.userSig()?.userId ?? null);

  readonly counts = computed(() => {
    const u = this.users();
    return {
      All: u.length,
      Admin: u.filter((x) => x.role === 'Admin').length,
      Lead: u.filter((x) => x.role === 'Lead').length,
      Tester: u.filter((x) => x.role === 'Tester').length,
      Viewer: u.filter((x) => x.role === 'Viewer').length,
      Invited: u.filter((x) => x.status === 'Invited').length,
    } as Record<RoleFilter, number>;
  });

  readonly filteredUsers = computed(() => {
    const filter = this.roleFilter();
    const term = this.search().trim().toLowerCase();
    return this.users()
      .filter((u) => {
        if (filter === 'All') return true;
        if (filter === 'Invited') return u.status === 'Invited';
        return u.role === filter;
      })
      .filter((u) => {
        if (!term) return true;
        const name = `${u.firstName} ${u.lastName}`.toLowerCase();
        return name.includes(term) || (u.email ?? '').toLowerCase().includes(term);
      });
  });

  ngOnInit(): void {
    this.load();
  }

  // ---- row helpers ----

  initials(u: User): string {
    const a = (u.firstName?.[0] ?? '').toUpperCase();
    const b = (u.lastName?.[0] ?? '').toUpperCase();
    return a + b || (u.email?.[0] ?? '?').toUpperCase();
  }

  isSelf(u: User): boolean {
    return u.userId != null && u.userId === this.currentUserId();
  }

  lastActiveLabel(u: User): string {
    if (u.status === 'Invited') return 'Pending';
    return timeAgo(u.lastActiveOnUtc);
  }

  // ---- actions ----

  openInvite(): void {
    this.dialog
      .open<InviteUserRequestResult, unknown, InviteUserDialogComponent>(InviteUserDialogComponent)
      .closed.subscribe((result) => {
        if (!result) return;
        this.adminUsers.invite(result).subscribe({
          next: () => {
            this.toast.success(`Invitation sent to ${result.email}.`);
            this.load();
          },
          error: () => this.toast.error('Failed to send the invitation.'),
        });
      });
  }

  changeRole(u: User, role: UserRole): void {
    if (this.isSelf(u) || u.userId == null || u.role === role) return;
    this.adminUsers.changeRole(u.userId, role).subscribe({
      next: () => {
        this.toast.success(`${displayName(u)} is now ${role}.`);
        this.load();
      },
      error: () => this.toast.error('Failed to change the role.'),
    });
  }

  setActive(u: User, active: boolean): void {
    if (this.isSelf(u) || u.userId == null) return;
    const call = active
      ? this.adminUsers.reactivate(u.userId)
      : this.adminUsers.deactivate(u.userId);
    call.subscribe({
      next: () => {
        this.toast.success(`${displayName(u)} ${active ? 'reactivated' : 'deactivated'}.`);
        this.load();
      },
      error: () => this.toast.error('Failed to update the user.'),
    });
  }

  resendInvite(u: User): void {
    if (u.userId == null) return;
    this.adminUsers.resendInvite(u.userId).subscribe({
      next: () => this.toast.success(`Invitation re-sent to ${u.email}.`),
      error: () => this.toast.error('Failed to re-send the invitation.'),
    });
  }

  async remove(u: User): Promise<void> {
    if (this.isSelf(u) || u.userId == null) return;
    const confirmed = await this.dialog.confirm({
      title: 'Remove user',
      message: `Remove ${displayName(u)}? They will lose access to BOLT TestHub.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!confirmed) return;
    this.adminUsers.remove(u.userId).subscribe({
      next: () => {
        this.toast.success(`${displayName(u)} removed.`);
        this.load();
      },
      error: () => this.toast.error('Failed to remove the user.'),
    });
  }

  private load(): void {
    this.isLoading.set(true);
    this.adminUsers
      .getAll()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (users) => this.users.set(users),
        error: () => this.toast.error('Failed to load users.'),
      });
  }
}

type InviteUserRequestResult =
  import('../../core/services/user/admin-users.service').InviteUserRequest;

function displayName(u: User): string {
  return `${u.firstName} ${u.lastName}`.trim() || (u.email ?? 'User');
}
